import { HighlightCard as HighlightCardType } from '@yleisradio/areena-types';
import { Container } from 'components/Container';
import { GridContainer, GridElement } from 'components/Grid';
import * as React from 'react';
import { sanitizeHtmlId } from 'utils/ui-helpers';
import styles from './HighlightCard.module.scss';
import { HighlightControls } from './HighlightControls';
import { fixedAspectRatioImageLoader, loaderUrl } from 'utils/cloudinary';
import Image from 'next/image';
import { idFromPointer } from 'utils/pointer';
import { ImpressionAnalyticsContextProvider } from 'contexts/ImpressionAnalyticsContext';
import { Observable as ImpressionObservable } from 'contexts/ImpressionAnalyticsContext/Observable';
import { AnalyticsContextProvider } from 'contexts/AnalyticsContext';
import { ViewableHighlightCard } from 'components/SnowplowEngagementAnalytics/ViewableHighlightCard';
import {
  useTrackCardClick,
  useViewUuid,
} from 'components/SnowplowEngagementAnalytics/hooks';

type ImageAspectRatio = '16:9' | '1:1';

function getSizes(aspectRatio: ImageAspectRatio): string {
  switch (aspectRatio) {
    case '1:1':
      return '(min-width: 1020px) 23.2vw, (min-width: 480px) 25.31vw, 50.625vw';

    case '16:9':
      return '(min-width: 1020px) 41.25vw, (min-width: 480px) 45vw, 90vw';
  }
}

type Props = {
  card: HighlightCardType;
  cardKey: string;
  contentIndex: number;
};

export const HighlightCard: React.FunctionComponent<Props> = ({
  card,
  cardKey,
  contentIndex,
}) => {
  const headingId = sanitizeHtmlId(cardKey + 'title');
  const controls = card.controls || [];
  const imageAspectRatio: ImageAspectRatio = card.style?.image || '16:9';
  const viewUuid = useViewUuid();
  const { trackCardClick, trackPlayButtonClick } = useTrackCardClick(viewUuid);

  return (
    <AnalyticsContextProvider context={card.analytics?.context?.comscore}>
      <ImpressionAnalyticsContextProvider
        uiElementPosition={contentIndex}
        uiElementType="highlightCard"
      >
        <ViewableHighlightCard
          card={card}
          contentIndex={contentIndex}
          viewUuid={viewUuid}
        >
          <div role="group" aria-labelledby={headingId} className={styles.root}>
            <Container>
              <GridContainer>
                <GridElement mobile={0} desktopSmall={1} />

                <GridElement
                  mobile={12}
                  mobileLandscape={6}
                  orderMobileLandscape={2}
                  desktopSmall={11}
                >
                  <div className={styles.imageContainer}>
                    {card.image && (
                      <Image
                        alt={card.image.alt || ''}
                        className={styles.image}
                        fill
                        loader={fixedAspectRatioImageLoader(imageAspectRatio)}
                        sizes={getSizes(imageAspectRatio)}
                        src={loaderUrl(card.image)}
                      />
                    )}
                  </div>
                </GridElement>

                <GridElement mobile={12} mobileLandscape={6} desktopSmall={10}>
                  <div className={styles.textContent}>
                    <h2 className={styles.title} id={headingId}>
                      {card.title}
                    </h2>
                    {card.description ? (
                      <p className={styles.description}>{card.description}</p>
                    ) : null}
                    <ImpressionObservable
                      yleId={idFromPointer(card.pointer) || undefined}
                      itemPosition={0}
                    >
                      {controls.length > 0 && (
                        <div className={styles.controls}>
                          <HighlightControls
                            controls={controls}
                            trackLinkClick={trackCardClick}
                            trackPlayClick={trackPlayButtonClick}
                          />
                        </div>
                      )}
                    </ImpressionObservable>
                  </div>
                </GridElement>

                <GridElement mobile={0} desktopSmall={1} />
              </GridContainer>
            </Container>
          </div>
        </ViewableHighlightCard>
      </ImpressionAnalyticsContextProvider>
    </AnalyticsContextProvider>
  );
};
